import {
  BottomNavigation,
  Button,
  Controller,
  FormDirtyModal,
  PageModal,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { Maybe, User } from 'src/graphql/generated/graphql';
import { useState } from 'react';
import { UsersServerSideField } from '../form/fields';

export enum CommentFieldType {
  Required = 'required',
  Optional = 'optional',
}

export type Form = {
  assignedToUserId: string | null;
  comment: string;
};

type AssignmentModalProps = {
  title: string;
  currentAssignment?: Maybe<Partial<User>>;
  commentField?: CommentFieldType;
  onClose: () => void;
  onSave: (data: Form) => void;
  loading: boolean;
};

export const UserAssignmentModal = ({
  title,
  currentAssignment,
  commentField,
  onClose,
  onSave,
  loading,
}: AssignmentModalProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'common.modals.user-assignment',
  });
  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Form>({
    defaultValues: {
      assignedToUserId: currentAssignment?.id || null,
      comment: '',
    },
  });

  const onCancel = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onClose?.();
    }
  };

  const onSubmit = (data: Form) => {
    const { assignedToUserId, ...rest } = data;

    onSave({
      assignedToUserId: assignedToUserId ?? null,
      ...rest,
    });
  };

  return (
    <PageModal open onClose={onCancel} title={title}>
      <div className="w-full min-w-small">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Controller
              name="assignedToUserId"
              control={control}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('choose-assignee')}
                  </Typography>
                  <UsersServerSideField field={field} options={[]} />
                </div>
              )}
            />
          </div>
          {commentField && (
            <div className="mb-4">
              <Controller
                name="comment"
                control={control}
                rules={{ required: commentField === CommentFieldType.Required }}
                render={({ field }) => (
                  <div>
                    <Typography className="mb-2" variant="subtitle1">
                      {t('add-comment')}
                      {commentField === CommentFieldType.Required && (
                        <span className="text-red-500"> *</span>
                      )}
                    </Typography>
                    <TextEditor {...field} defaultValue={field.value} />
                  </div>
                )}
              />
            </div>
          )}
          <BottomNavigation>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {t('assign')}
            </Button>
            <Button variant="outlined" onClick={onCancel}>
              {t('close')}
            </Button>
          </BottomNavigation>
        </form>
      </div>
      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={onClose}
      />
    </PageModal>
  );
};
